import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../lib/api'
import { useTestDataVisibility } from '../context/TestDataVisibilityContext'
import type {
  Demand,
  DemandCreateInput,
  DemandFieldAuditEntry,
  DemandListResponse,
  DemandPromoteInput,
  DemandResolveInput,
  DemandRouteInput,
  DemandStaffInput,
  DemandStageHistoryEntry,
  DemandTransitionInput,
  DemandUpdateInput,
  EngagementEvent,
} from '../types/demand'

interface ListFilters {
  stage?: string
  focus_area?: string
  search?: string
  created_by?: number
  /** Only demands the current user submitted (their own, any stage). */
  mine_submissions?: boolean
  /** Only demands the caller caretakes (#1208). */
  mine_caretaker?: boolean
  caretaker?: string
  page?: number
  page_size?: number
  /** Fetch every page before client-side ranking or filtering. */
  allPages?: boolean
  /** Admin-only: include soft-deleted rows. Backend 403s for non-admins. */
  include_deleted?: boolean
  /** Server-side field filters (#1269) — the demandFilters.ts vocabulary,
   *  applied before the count and the page so `total` is the truth. */
  demand_type?: string
  support_mode?: string
  staffing?: string
  tag_id?: number
  bucket?: string
  /** The list's two quick presets, server-side (#1269). */
  posted_without_applications?: boolean
  stale_days?: number
  /**
   * 'review' orders by how suspect a landing looks — an overridden suggestion
   * first, then anything the classifier could not vouch for, then unclassified,
   * then oldest. The audit queue's order. Anything else keeps newest-first.
   *
   * Ordered server-side on purpose: sorting the fetched page in the browser
   * would only order the rows that came back, which is the truncation #1269
   * is about.
   */
  sort?: 'review' | 'last_activity' | 'last_activity_asc'
  /** Optional React Query gate for pages that inject preloaded data in tests. */
  enabled?: boolean
}

const QUERY_KEYS = {
  list: (filters: ListFilters) => ['demands', filters] as const,
  detail: (id: number | string) => ['demand', id] as const,
  history: (id: number | string) => ['demand-history', id] as const,
  fieldAudit: (id: number | string) => ['demand-field-audit', id] as const,
  engagement: (id: number | string) => ['demand-engagement', id] as const,
}

/**
 * Every cache keyed on a single demand.
 *
 * React Query matches key prefixes element by element, so `['demand']` does NOT
 * reach `['demand-readiness', id]` — the strings differ at position 0. That is
 * not obvious, and it is why three of these keys spent months never being
 * invalidated by anything: the readiness checklist kept showing a field as
 * missing after it was filled in, and the Timeline's field-edit and engagement
 * panes kept showing a demand's history as it was at first paint.
 *
 * The list exists so the next per-demand query is added HERE rather than
 * silently becoming the fourth. `invalidateDemandScoped` is what mutations
 * should call; `demandScopedKeysAreCovered` in the tests pins the two together.
 */
export const READINESS_QUERY_PREFIX = 'demand-readiness'

export const DEMAND_SCOPED_PREFIXES = [
  'demand',
  'demand-history',
  'demand-field-audit',
  'demand-engagement',
  READINESS_QUERY_PREFIX,
] as const

/** Everything `invalidateAll` knocks down — the list key and the FA workspace
 *  alongside the per-demand caches. Exported so the test can check coverage
 *  against the same list the code uses, rather than a second copy of it. */
export const INVALIDATED_PREFIXES = [
  'demands',
  'fa-workspace',
  // Routing a demand onward changes what the Digital Council sees (#1293).
  'digital-council-queue',
  ...DEMAND_SCOPED_PREFIXES,
] as const

function invalidateAll(qc: ReturnType<typeof useQueryClient>) {
  for (const prefix of INVALIDATED_PREFIXES) {
    qc.invalidateQueries({ queryKey: [prefix], exact: false })
  }
}

export function useDemands(filters: ListFilters = {}, retainForViewer?: string) {
  const { showTestData } = useTestDataVisibility()
  return useQuery<DemandListResponse>({
    queryKey: [...QUERY_KEYS.list(filters), { includeTest: showTestData }, ...(retainForViewer ? [retainForViewer] : [])],
    // Opt-in for the list: keep rows during filter changes, but never across
    // an identity/permission change or the real/test-data boundary.
    placeholderData: retainForViewer ? (previous, query) => {
      const previousVisibility = query?.queryKey[2]
      return query?.queryKey[3] === retainForViewer &&
        previousVisibility && typeof previousVisibility === 'object' &&
        'includeTest' in previousVisibility && previousVisibility.includeTest === showTestData
        ? previous : undefined
    } : undefined,
    queryFn: async ({ signal }) => {
      const params: Record<string, string | number> = {}
      if (filters.stage) params.stage = filters.stage
      if (filters.focus_area) params.focus_area = filters.focus_area
      if (filters.search) params.search = filters.search
      if (filters.created_by) params.created_by = filters.created_by
      if (filters.mine_submissions) params.mine_submissions = 'true'
      if (filters.mine_caretaker) params.mine_caretaker = 'true'
      if (filters.caretaker) params.caretaker = filters.caretaker
      params.page = filters.allPages ? 1 : (filters.page ?? 1)
      params.page_size = filters.page_size ?? 50
      // Exclusive: include_test=true → ONLY test rows; false → ONLY real.
      params.include_test = showTestData ? 'true' : 'false'
      if (filters.include_deleted) params.include_deleted = 'true'
      if (filters.sort) params.sort = filters.sort
      if (filters.demand_type) params.demand_type = filters.demand_type
      if (filters.support_mode) params.support_mode = filters.support_mode
      if (filters.staffing) params.staffing = filters.staffing
      if (filters.tag_id != null) params.tag_id = filters.tag_id
      if (filters.bucket) params.bucket = filters.bucket
      if (filters.posted_without_applications) params.posted_without_applications = 'true'
      if (filters.stale_days != null) params.stale_days = filters.stale_days
      const { data } = await api.get<DemandListResponse>('/demands', { params, signal })
      if (!filters.allPages) return data
      const items = [...data.items]
      let total = data.total
      while (Number(params.page) * Number(params.page_size) < total) {
        params.page = Number(params.page) + 1
        const next = await api.get<DemandListResponse>('/demands', { params, signal })
        total = next.data.total
        if (!next.data.items.length && items.length < total) {
          throw new Error('Could not load the complete demand list. Please retry.')
        }
        items.push(...next.data.items)
      }
      if (items.length !== total || new Set(items.map((item) => item.id)).size !== total) {
        throw new Error('The demand list changed while loading. Please retry.')
      }
      return { ...data, items, total }
    },
    staleTime: 30_000,
    enabled: filters.enabled ?? true,
  })
}

export function useDemand(id: number | string | undefined) {
  return useQuery<Demand>({
    queryKey: QUERY_KEYS.detail(id ?? ''),
    queryFn: async () => {
      const res = await api.get(`/demands/${id}`)
      return res.data
    },
    enabled: !!id,
  })
}

export interface BroadcastReach {
  focus_area_code: string | null
  fa_talent: number
  all_talent: number
  /**
   * Whether talent can declare this focus area at all. False for BO
   * (council-only) and UN (the unclassified bucket), where `fa_talent` is not
   * a small number but an inapplicable one — and a bare 0 reads as "nobody is
   * interested" rather than "this option does not exist here".
   */
  fa_talent_scoped: boolean
}

/**
 * How many people each broadcast audience would actually reach.
 *
 * The two options in the posting dialog read as equivalent while differing by
 * more than an order of magnitude — one real posting went to 6 people because
 * its author took the default, where the other choice would have reached 208.
 * Fetched only while the dialog is open on a posting move, and computed server
 * side by the same function the send uses, so the number cannot promise more
 * than it delivers.
 */
export function useBroadcastReach(id: number | string | undefined, enabled = true) {
  return useQuery<BroadcastReach>({
    queryKey: [...QUERY_KEYS.detail(id ?? ''), 'broadcast-reach'],
    queryFn: async () => {
      const res = await api.get(`/demands/${id}/broadcast-reach`)
      return res.data
    },
    enabled: !!id && enabled,
    staleTime: 30_000,
  })
}

export function useDemandHistory(id: number | string | undefined) {
  return useQuery<DemandStageHistoryEntry[]>({
    queryKey: QUERY_KEYS.history(id ?? ''),
    queryFn: async () => {
      const res = await api.get(`/demands/${id}/history`)
      return res.data
    },
    enabled: !!id,
  })
}

export function useDemandFieldAudit(id: number | string | undefined) {
  return useQuery<DemandFieldAuditEntry[]>({
    queryKey: QUERY_KEYS.fieldAudit(id ?? ''),
    queryFn: async () => {
      const res = await api.get(`/demands/${id}/field-audit`)
      return res.data
    },
    enabled: !!id,
  })
}

export function useDemandEngagement(id: number | string | undefined) {
  return useQuery<EngagementEvent[]>({
    queryKey: QUERY_KEYS.engagement(id ?? ''),
    queryFn: async () => {
      const res = await api.get(`/demands/${id}/engagement-activity`)
      return res.data
    },
    enabled: !!id,
  })
}

export function useCreateDemand() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: DemandCreateInput) => {
      const res = await api.post('/demands', input)
      return res.data as Demand
    },
    onSuccess: () => invalidateAll(qc),
  })
}

export interface DemandSubmitInput {
  title: string
  description: string
  issue_problem?: string
  target_user?: string
  background?: string
  anticipated_benefit?: string
  business_criticality?: string
  department?: string
  /** One-time key minted when the form opened; a reload/retry reuses it so the
   *  server lands one record (#1264). No focus area and no suggestion echo:
   *  the server classifies and routes every submission itself (UAT
   *  2026-09-11) — the form sends content only. */
  idempotency_key?: string
}

/**
 * Self-service demand submission — any logged-in user. Hits POST
 * /demands/submit (open to all roles), unlike useCreateDemand (council/admin).
 */
export function useSubmitDemand() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: DemandSubmitInput) => {
      const res = await api.post('/demands/submit', input)
      return res.data as Demand
    },
    onSuccess: () => invalidateAll(qc),
  })
}

export function useUpdateDemand() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: DemandUpdateInput }) => {
      const res = await api.patch(`/demands/${id}`, data)
      return res.data as Demand
    },
    onSuccess: (_data, vars) => {
      invalidateAll(qc)
      qc.invalidateQueries({ queryKey: QUERY_KEYS.detail(vars.id) })
    },
  })
}

interface DeleteDemandArgs {
  id: number
  reason: string
}

export function useDeleteDemand() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reason }: DeleteDemandArgs) => {
      // Axios needs `data` to send a body on DELETE.
      await api.delete(`/demands/${id}`, { data: { reason } })
    },
    onSuccess: () => invalidateAll(qc),
  })
}

export function useRestoreDemand() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api.post(`/demands/${id}/restore`)
      return res.data as Demand
    },
    onSuccess: (_data, id) => {
      invalidateAll(qc)
      qc.invalidateQueries({ queryKey: QUERY_KEYS.detail(id) })
      qc.invalidateQueries({ queryKey: QUERY_KEYS.history(id) })
    },
  })
}

interface InviteTalentsArgs {
  demandId: number
  talentIds: number[]
  note?: string
}

interface InviteTalentsResponse {
  demand_id: number
  invited: number[]
  skipped_already_invited: number[]
  total_requested: number
}

export function useInviteTalents() {
  const qc = useQueryClient()
  return useMutation<InviteTalentsResponse, Error, InviteTalentsArgs>({
    mutationFn: async ({ demandId, talentIds, note }) => {
      const res = await api.post(`/invitations`, {
        demand_id: demandId,
        talent_ids: talentIds,
        message: note,
      })
      return res.data as InviteTalentsResponse
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['invitations'] })
      qc.invalidateQueries({ queryKey: QUERY_KEYS.detail(vars.demandId) })
    },
  })
}

export interface AddTeamMemberArgs {
  demandId: number
  /** A directory talent → approved application. */
  talentId?: number
  /** A freehand name → stored on the demand (display-only). */
  name?: string
  note?: string
}

/**
 * Council manually seats a team member, bypassing advertise/apply/approve.
 * Pass exactly one of `talentId` (directory talent) or `name` (freehand).
 */
export function useAddTeamMember() {
  const qc = useQueryClient()
  return useMutation<Demand, Error, AddTeamMemberArgs>({
    mutationFn: async ({ demandId, talentId, name, note }) => {
      const res = await api.post(`/demands/${demandId}/members`, {
        talent_id: talentId,
        name,
        note,
      })
      return res.data as Demand
    },
    onSuccess: (_data, vars) => {
      invalidateAll(qc)
      qc.invalidateQueries({ queryKey: QUERY_KEYS.detail(vars.demandId) })
      // Contributors + Applicants are derived from approved applications.
      qc.invalidateQueries({ queryKey: ['applications'] })
    },
  })
}

export interface FreehandSuggestion {
  freehand_name: string
  candidates: { id: number; name: string; email: string; score: number }[]
}

/** Fuzzy account suggestions for a demand's freehand member names (council). */
export function useFreehandSuggestions(demandId: number, enabled = true) {
  return useQuery<FreehandSuggestion[]>({
    queryKey: ['demand', demandId, 'freehand-suggestions'],
    enabled,
    queryFn: async () => (await api.get(`/demands/${demandId}/freehand-suggestions`)).data,
  })
}

/** Link a freehand member name to a directory account (council own-FA / admin). */
export function useLinkFreehandMember() {
  const qc = useQueryClient()
  return useMutation<Demand, Error, { demandId: number; freehandName: string; talentId: number }>({
    mutationFn: async ({ demandId, freehandName, talentId }) => {
      const res = await api.post(`/demands/${demandId}/members/link`, {
        freehand_name: freehandName,
        talent_id: talentId,
      })
      return res.data as Demand
    },
    onSuccess: (_data, vars) => {
      invalidateAll(qc)
      qc.invalidateQueries({ queryKey: QUERY_KEYS.detail(vars.demandId) })
      qc.invalidateQueries({ queryKey: ['applications'] })
      qc.invalidateQueries({ queryKey: ['demand', vars.demandId, 'freehand-suggestions'] })
    },
  })
}

/**
 * Replace a demand's full set of (active) tags. Council own-FA / admin only;
 * each tag must belong to the demand's own focus area.
 */
export function useSetDemandTags() {
  const qc = useQueryClient()
  return useMutation<Demand, Error, { demandId: number; tagIds: number[] }>({
    mutationFn: async ({ demandId, tagIds }) => {
      const res = await api.put(`/demands/${demandId}/tags`, { tag_ids: tagIds })
      return res.data as Demand
    },
    onSuccess: (_data, vars) => {
      invalidateAll(qc)
      qc.invalidateQueries({ queryKey: QUERY_KEYS.detail(vars.demandId) })
    },
  })
}

export function useTransitionDemand() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: DemandTransitionInput }) => {
      const res = await api.post(`/demands/${id}/transition`, body)
      return res.data as Demand
    },
    onSuccess: (_data, vars) => {
      invalidateAll(qc)
      qc.invalidateQueries({ queryKey: QUERY_KEYS.detail(vars.id) })
      qc.invalidateQueries({ queryKey: QUERY_KEYS.history(vars.id) })
    },
  })
}

export function useResolveDemand() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: DemandResolveInput }) => {
      const res = await api.post(`/demands/${id}/resolve`, body)
      return res.data as Demand
    },
    onSuccess: (_data, vars) => {
      invalidateAll(qc)
      qc.invalidateQueries({ queryKey: QUERY_KEYS.detail(vars.id) })
      qc.invalidateQueries({ queryKey: QUERY_KEYS.history(vars.id) })
    },
  })
}

export function useChangeResolution() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: DemandResolveInput }) => {
      const res = await api.patch(`/demands/${id}/resolution`, body)
      return res.data as Demand
    },
    onSuccess: (_data, vars) => {
      invalidateAll(qc)
      qc.invalidateQueries({ queryKey: QUERY_KEYS.detail(vars.id) })
      qc.invalidateQueries({ queryKey: QUERY_KEYS.history(vars.id) })
    },
  })
}

export function useReopenDemand() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      id,
      reason,
      toStage,
    }: {
      id: number
      reason: string
      toStage: string
    }) => {
      const res = await api.post(`/demands/${id}/reopen`, { reason, to_stage: toStage })
      return res.data as Demand
    },
    onSuccess: (_data, vars) => {
      invalidateAll(qc)
      qc.invalidateQueries({ queryKey: QUERY_KEYS.detail(vars.id) })
      qc.invalidateQueries({ queryKey: QUERY_KEYS.history(vars.id) })
    },
  })
}

// ── Withdraw (requestor pulls out; council confirms/declines a pending one) ──

function _invalidateDemand(qc: ReturnType<typeof useQueryClient>, id: number) {
  invalidateAll(qc)
  qc.invalidateQueries({ queryKey: QUERY_KEYS.detail(id) })
  qc.invalidateQueries({ queryKey: QUERY_KEYS.history(id) })
}

export function useWithdrawDemand() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason: string }) => {
      const res = await api.post(`/demands/${id}/withdraw`, { reason })
      return res.data as Demand
    },
    onSuccess: (_data, vars) => _invalidateDemand(qc, vars.id),
  })
}

export function useConfirmWithdrawal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const res = await api.post(`/demands/${id}/withdraw/confirm`, {})
      return res.data as Demand
    },
    onSuccess: (_data, vars) => _invalidateDemand(qc, vars.id),
  })
}

export function useDeclineWithdrawal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const res = await api.post(`/demands/${id}/withdraw/decline`, {})
      return res.data as Demand
    },
    onSuccess: (_data, vars) => _invalidateDemand(qc, vars.id),
  })
}

export function useStaffDemand() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: DemandStaffInput }) => {
      const res = await api.post(`/demands/${id}/staff`, body)
      return res.data as Demand
    },
    onSuccess: (_data, vars) => {
      invalidateAll(qc)
      qc.invalidateQueries({ queryKey: QUERY_KEYS.detail(vars.id) })
      qc.invalidateQueries({ queryKey: QUERY_KEYS.history(vars.id) })
    },
  })
}

/**
 * Say where a demand belongs: a focus area, or the Digital Council.
 *
 * Not a PATCH of `focus_area_code` — that field is deny-listed server-side,
 * because the change reassigns which council answers for the demand and it
 * requires a reason a field edit has nowhere to put.
 */
export function useConfirmLanding() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      const res = await api.post(`/demands/${id}/confirm-landing`)
      return res.data as Demand
    },
    onSuccess: (_data, id) => {
      invalidateAll(qc)
      qc.invalidateQueries({ queryKey: QUERY_KEYS.detail(id) })
      qc.invalidateQueries({ queryKey: QUERY_KEYS.fieldAudit(id) })
    },
  })
}

export function useRouteDemand() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: DemandRouteInput }) => {
      const res = await api.post(`/demands/${id}/route`, body)
      return res.data as Demand
    },
    onSuccess: (_data, vars) => {
      invalidateAll(qc)
      qc.invalidateQueries({ queryKey: QUERY_KEYS.detail(vars.id) })
      qc.invalidateQueries({ queryKey: QUERY_KEYS.fieldAudit(vars.id) })
    },
  })
}

export function usePromoteDemand() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: DemandPromoteInput }) => {
      const res = await api.post(`/demands/${id}/promote`, body)
      return res.data as Demand
    },
    onSuccess: (_data, vars) => {
      invalidateAll(qc)
      qc.invalidateQueries({ queryKey: QUERY_KEYS.detail(vars.id) })
      qc.invalidateQueries({ queryKey: QUERY_KEYS.history(vars.id) })
    },
  })
}

export function useReorderDemands() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (items: Array<{ id: number; rank_order: number }>) => {
      const res = await api.patch('/demands/reorder', { items })
      return res.data as { updated: number }
    },
    onSuccess: () => invalidateAll(qc),
  })
}

export interface ClassifyFocusAreaInput {
  title: string
  issue_problem: string
  target_user?: string
  background?: string
  anticipated_benefit?: string
}

export interface ClassifyFocusAreaResult {
  /** A focus area code, or 'DC' when the answer is "not this council's work".
   *  null means no answer — which is not the same as a low-confidence one. */
  suggested_code: string | null
  reasoning: string
  /** The model's own estimate, 0-1. Not calibrated; stored so #1300 can find
   *  out what it is worth. Nothing routes on it. */
  confidence: number | null
  llm_available: boolean
}

export function useClassifyFocusArea() {
  return useMutation({
    mutationFn: async (input: ClassifyFocusAreaInput): Promise<ClassifyFocusAreaResult> => {
      const res = await api.post('/focus-areas/classify', input)
      return res.data as ClassifyFocusAreaResult
    },
  })
}
